import AutorenewIcon from '@mui/icons-material/Autorenew';
import { Button } from '@efexteam/components';
import {
  PayableTypeFilter,
  PayableTypes,
  TabsPayableFilter,
} from '@efex-services/client/types';
import useBoolean from '@main-hooks/useBoolean';
import clsx from 'clsx';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectProfileData } from '@pages/profile/store/selectors';
import { Actions, Can, Subjects, useRole } from '@efexteam/permission-manager';
import { Box } from '@mui/material';

export interface ITabSelected {
  tab: PayableTypeFilter | TabsPayableFilter;
  isBulkPayment: boolean;
}

interface IPaymentTabs {
  payableType: PayableTypeFilter | TabsPayableFilter;
  bulkPayment: boolean;
  isLoadingPayments: boolean;
  onTabSelected: (data: ITabSelected) => void;
}

const PaymentTabs = ({
  payableType,
  bulkPayment,
  isLoadingPayments,
  onTabSelected,
}: IPaymentTabs) => {
  const { t } = useTranslation();
  const userProfile = useSelector(selectProfileData);
  const { isReadOnly } = useRole();
  const [
    isBulksPaymentTabVisible,
    { setFalse: hideBulksPaymentTab, setTrue: showBulksPaymentTab },
  ] = useBoolean(false);

  useEffect(() => {
    if (!userProfile?.isFullRegister || isReadOnly) {
      hideBulksPaymentTab();
    } else {
      showBulksPaymentTab();
    }
  }, [userProfile, isReadOnly, hideBulksPaymentTab, showBulksPaymentTab]);

  return (
    <Box className="py-2">
      <Box
        component="ul"
        className="flex space-x-2 m-0 bg-primary-fill-dark pt-1"
      >
        <Box
          component="li"
          className={clsx(
            'px-3 py-2 transition-all',
            payableType === PayableTypes.PROCCESS && !bulkPayment
              ? 'bg-primary-fill-light'
              : ''
          )}
        >
          <Button
            onClick={() => {
              onTabSelected({
                tab: PayableTypes.PROCCESS,
                isBulkPayment: false,
              });
            }}
            appearance="primary"
            variant="text"
            disabled={isLoadingPayments}
            className={clsx(
              'm-0',
              isLoadingPayments ? '!bg-transparent !text-slate-200' : '',
              payableType !== PayableTypes.PROCCESS || bulkPayment
                ? '!text-white'
                : ''
            )}
          >
            {isLoadingPayments ? (
              <AutorenewIcon className="animate-spin mr-2" />
            ) : null}
            {t('proccesingPayment')}
          </Button>
        </Box>
        {/* TODO: Remove this when lending deprecation is confirmed */}
        <Box
          component="li"
          className={clsx(
            'px-3 py-2 transition-all !hidden',
            payableType === PayableTypes.LENDING && !bulkPayment
              ? 'bg-primary-fill-light'
              : ''
          )}
        >
          <Button
            onClick={() => {
              onTabSelected({
                tab: PayableTypes.LENDING,
                isBulkPayment: false,
              });
            }}
            appearance="primary"
            variant="text"
            disabled={isLoadingPayments}
            className={clsx(
              'm-0',
              isLoadingPayments ? '!bg-transparent !text-slate-200' : '',
              payableType !== PayableTypes.LENDING || bulkPayment
                ? '!text-white'
                : ''
            )}
          >
            {isLoadingPayments ? (
              <AutorenewIcon className="animate-spin mr-2" />
            ) : null}
            {t('ldTblSelector')}
          </Button>
        </Box>
        {isBulksPaymentTabVisible ? (
          <Can action={Actions.CREATE} subject={Subjects.BULK_PAYMENTS}>
            <Box
              component="li"
              className={clsx(
                'p-2 transition-all',
                bulkPayment ? 'bg-primary-fill-light' : ''
              )}
            >
              <Button
                onClick={() =>
                  onTabSelected({ tab: 'BULKS', isBulkPayment: true })
                }
                appearance="primary"
                variant="text"
                disabled={isLoadingPayments || !userProfile?.canUserWithdraw}
                className={clsx(
                  'm-0',
                  isLoadingPayments ? '!bg-transparent !text-slate-200' : '',
                  !bulkPayment ? '!text-white' : ''
                )}
              >
                {isLoadingPayments ? (
                  <AutorenewIcon className="animate-spin mr-2" />
                ) : null}
                {t('bulkPayments')}
              </Button>
            </Box>
          </Can>
        ) : null}
      </Box>
    </Box>
  );
};

export default PaymentTabs;
